import React, {
  useEffect,
  useState,
} from 'react';

import './FollowList.css';

import AvatarPreview from '../avatar/AvatarPreview';
import { defaultAvatar } from '../avatar/avatarConfig';
import { followUser, unfollowUser } from './followApi';

const apiUrl =
  process.env.REACT_APP_API_URL;

const FollowList = ({
  initialTab = 'following',
  onBack,
  onOpenProfile,
}) => {
  const [activeTab, setActiveTab] =
    useState(initialTab);

  const [users, setUsers] =
    useState([]);

  const [isLoading, setIsLoading] =
    useState(true);

  const [errorMessage, setErrorMessage] =
    useState('');

  // Persoanele la care ai renuntat in sesiunea asta raman in lista (cu butonul
  // intors pe URMARESTE), ca o apasare gresita sa se poata anula pe loc.
  // Dispar la urmatoarea incarcare.
  const [unfollowedIds, setUnfollowedIds] =
    useState([]);

  const [busyIds, setBusyIds] =
    useState([]);

  // ==========================================
  // INCARCARE UTILIZATORI
  // ==========================================

  useEffect(() => {
    const fetchUsers = async () => {
      setIsLoading(true);
      setErrorMessage('');
      setUnfollowedIds([]);

      try {
        const endpoint =
          activeTab === 'following'
            ? '/user/following'
            : '/user/followers';

        const response = await fetch(
          `${apiUrl}${endpoint}`,
          {
            method: 'GET',
            credentials: 'include',
          }
        );

        const contentType =
          response.headers.get(
            'content-type'
          );

        if (
          !contentType ||
          !contentType.includes(
            'application/json'
          )
        ) {
          throw new Error(
            'Serverul nu a returnat un raspuns valid.'
          );
        }

        const data =
          await response.json();

        if (!response.ok) {
          throw new Error(
            data?.message ||
            'Lista nu a putut fi incarcata.'
          );
        }

        setUsers(
          Array.isArray(data)
            ? data
            : []
        );

      } catch (error) {
        console.error(
          'Eroare lista follow:',
          error
        );

        setUsers([]);

        setErrorMessage(
          error.message ||
          'Lista nu a putut fi incarcata.'
        );

      } finally {
        setIsLoading(false);
      }
    };

    fetchUsers();
  }, [activeTab]);

  // ==========================================
  // SINCRONIZARE CU CELELALTE SUPRAFETE
  // ==========================================
  // Cardul din dreapta poate anula/relua o urmarire in timp ce lista e
  // deschisa; tinem pastilele in pas cu el prin evenimentul `followUpdated`.

  useEffect(() => {
    const handleFollowUpdated = (
      event
    ) => {
      const action =
        event?.detail?.action;
      const userId =
        event?.detail?.userId;

      if (!userId) {
        return;
      }

      if (action === 'unfollow') {
        setUnfollowedIds((current) =>
          current.includes(userId)
            ? current
            : [...current, userId]
        );
      } else if (action === 'follow') {
        setUnfollowedIds((current) =>
          current.filter(
            (id) => id !== userId
          )
        );
      }
    };

    window.addEventListener(
      'followUpdated',
      handleFollowUpdated
    );

    return () => {
      window.removeEventListener(
        'followUpdated',
        handleFollowUpdated
      );
    };
  }, []);

  // ==========================================
  // DESCHIDE PROFIL UTILIZATOR
  // ==========================================

  const handleOpenProfile = (
    userId
  ) => {
    if (!userId) {
      return;
    }

    if (onOpenProfile) {
      onOpenProfile(userId);
    }
  };

  // ==========================================
  // URMARESTE / NU MAI URMARI
  // ==========================================

  const handleToggleFollow = async (
    userId
  ) => {
    if (busyIds.includes(userId)) {
      return;
    }

    const wasUnfollowed =
      unfollowedIds.includes(userId);

    setBusyIds((current) => [
      ...current,
      userId,
    ]);

    try {
      if (wasUnfollowed) {
        await followUser(userId);

        setUnfollowedIds((current) =>
          current.filter(
            (id) => id !== userId
          )
        );
      } else {
        await unfollowUser(userId);

        setUnfollowedIds((current) => [
          ...current,
          userId,
        ]);
      }

    } catch (error) {
      console.error(
        'Eroare follow/unfollow:',
        error
      );

    } finally {
      setBusyIds((current) =>
        current.filter(
          (id) => id !== userId
        )
      );
    }
  };

  // ==========================================
  // RETURN
  // ==========================================

  return (
    <div className="follow-list-page">

      {/* HEADER */}

      <div className="follow-list-topbar">

        <button
          type="button"
          className="follow-list-back"
          onClick={onBack}
          aria-label="Inapoi"
        >
          ‹
        </button>

        <h1>
          {activeTab === 'following'
            ? 'Urmariti'
            : 'Urmaritori'}
        </h1>

        <div className="follow-list-topbar-spacer" />

      </div>

      {/* TABURI */}

      <div className="follow-list-tabs">

        <button
          type="button"
          className={
            `follow-list-tab ${
              activeTab === 'following'
                ? 'active'
                : ''
            }`
          }
          onClick={() =>
            setActiveTab('following')
          }
        >
          URMARITI
        </button>

        <button
          type="button"
          className={
            `follow-list-tab ${
              activeTab === 'followers'
                ? 'active'
                : ''
            }`
          }
          onClick={() =>
            setActiveTab('followers')
          }
        >
          URMARITORI
        </button>

      </div>

      {/* CONTINUT */}

      <div className="follow-list-content">

        {isLoading ? (

          <div className="follow-list-state">
            Se incarca...
          </div>

        ) : errorMessage ? (

          <div className="follow-list-state">
            {errorMessage}
          </div>

        ) : users.length === 0 ? (

          <div className="follow-list-state">

            {activeTab === 'following'
              ? 'Nu urmaresti momentan nicio persoana.'
              : 'Nu ai momentan urmaritori.'}

          </div>

        ) : (

          <div className="follow-list-items">

            {users.map((user) => {

              const userAvatar = {
                ...defaultAvatar,
                ...(user.avatar_config || {}),
              };

              const isFollowing =
                !unfollowedIds.includes(
                  user.user_id
                );

              const isBusy =
                busyIds.includes(
                  user.user_id
                );

              return (
                <div
                  className="follow-list-item"
                  key={user.user_id}
                >

                  {/* PROFIL CLICKABIL */}

                  <button
                    type="button"
                    className="follow-list-profile"
                    onClick={() =>
                      handleOpenProfile(
                        user.user_id
                      )
                    }
                    aria-label={
                      `Deschide profilul ${
                        user.display_name ||
                        'utilizatorului'
                      }`
                    }
                  >

                    {/* AVATAR */}

                    <div className="follow-list-avatar">

                      <AvatarPreview
                        avatar={userAvatar}
                        className="avatar-preview-follow-list"
                      />

                    </div>

                    {/* NUME */}

                    <div className="follow-list-info">

                      <div className="follow-list-name">
                        {user.display_name ||
                          'Utilizator'}
                      </div>

                      <div className="follow-list-username">
                        @{user.display_name ||
                          'utilizator'}
                      </div>

                    </div>

                  </button>

                  {/* XP */}

                  <div className="follow-list-xp">

                    {Number(
                      user.xp || 0
                    ).toLocaleString(
                      'ro-RO'
                    )}{' '}
                    XP

                  </div>

                  {/* URMARESTE / NU MAI URMARI */}

                  {activeTab === 'following' && (

                    <button
                      type="button"
                      className={
                        `follow-list-follow ${
                          isFollowing
                            ? 'follow-list-following'
                            : ''
                        }`
                      }
                      disabled={isBusy}
                      onClick={() =>
                        handleToggleFollow(
                          user.user_id
                        )
                      }
                      aria-label={
                        isFollowing
                          ? `Nu mai urmari ${
                              user.display_name ||
                              'utilizatorul'
                            }`
                          : `Urmareste ${
                              user.display_name ||
                              'utilizatorul'
                            }`
                      }
                    >

                      {isBusy
                        ? '...'
                        : isFollowing
                          ? 'URMARESTI'
                          : 'URMARESTE'}

                    </button>

                  )}

                </div>
              );
            })}

          </div>

        )}

      </div>

    </div>
  );
};

export default FollowList;